import { MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router';
import { ApiError } from '@/api/client';
import { AuthProvider } from '@/auth/AuthProvider';
import { RequireAuth, RequireClient, RequirePermission, RequireStaff } from '@/auth/guards';
import { AccessControlPage } from '@/features/access/AccessControlPage';
import { ActivityLogPage } from '@/features/activity/ActivityLogPage';
import { ChangePasswordPage } from '@/features/auth/ChangePasswordPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { ProfilePage } from '@/features/auth/ProfilePage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { DataBankPage } from '@/features/databank/DataBankPage';
import { NotFoundPage } from '@/features/errors/ErrorPages';
import { ExporterDetailPage } from '@/features/exporters/ExporterDetailPage';
import { ExportersPage } from '@/features/exporters/ExportersPage';
import { LibraryPage } from '@/features/library/LibraryPage';
import { NotificationsPage } from '@/features/notifications/NotificationsPage';
import { ClientAccountPage } from '@/features/portal/ClientAccountPage';
import { ClientDashboardPage } from '@/features/portal/ClientDashboardPage';
import { ClientDossiersPage } from '@/features/portal/ClientDossiersPage';
import { ClientDownloadsPage } from '@/features/portal/ClientDownloadsPage';
import { ClientLayout } from '@/features/portal/ClientLayout';
import { ClientLoginPage } from '@/features/portal/ClientLoginPage';
import { ClientNewRequestPage } from '@/features/portal/ClientNewRequestPage';
import { ClientNotificationsPage } from '@/features/portal/ClientNotificationsPage';
import { ClientRequisitionsPage } from '@/features/portal/ClientRequisitionsPage';
import { ClientRequestPage } from '@/features/portal/ClientRequestPage';
import { NewRequestPage } from '@/features/requests/NewRequestPage';
import { RequestDetailPage } from '@/features/requests/RequestDetailPage';
import { RequestsPage } from '@/features/requests/RequestsPage';
import { MyTasksPage } from '@/features/tasks/MyTasksPage';
import { TaskPage } from '@/features/tasks/TaskPage';
import { UsersPage } from '@/features/users/UsersPage';
import { AppLayout } from '@/layout/AppLayout';
import { RealtimeBridge } from '@/realtime/RealtimeBridge';
import { theme } from '@/theme';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // Do not retry authorization or validation problems; they will not fix themselves.
      retry: (failures, error) => !(error instanceof ApiError && error.status < 500) && failures < 2,
    },
  },
});

/** Providers that need the router (navigation inside auth handlers). */
function Root() {
  return (
    <AuthProvider>
      <RealtimeBridge />
      <Outlet />
    </AuthProvider>
  );
}

const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      // Development only: the signed-in portal pages with sample data (stripped from production builds).
      ...(import.meta.env.DEV
        ? [{ path: '/__preview/portal/*', lazy: async () => ({ Component: (await import('@/features/portal/dev/PortalPreview')).PortalPreview }) }]
        : []),
      // Clients sign in at /login, staff at /internal/login; the forced password change is shared.
      { path: '/login', element: <ClientLoginPage /> },
      { path: '/internal/login', element: <LoginPage /> },
      {
        path: '/change-password',
        element: (
          <RequireAuth>
            <ChangePasswordPage />
          </RequireAuth>
        ),
      },
      // Staff app. Client (exporter) logins are sent to their portal.
      {
        path: '/internal',
        element: (
          <RequireAuth loginPath="/internal/login">
            <RequireStaff>
              <AppLayout />
            </RequireStaff>
          </RequireAuth>
        ),
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'notifications', element: <NotificationsPage /> },
          { path: 'profile', element: <ProfilePage /> },
          {
            path: 'requests',
            element: (
              <RequirePermission module="DossierRequests">
                <RequestsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'requests/new',
            element: (
              <RequirePermission module="DossierRequests" action="edit" roles={['super_admin', 'admin', 'mis', 'ra']}>
                <NewRequestPage />
              </RequirePermission>
            ),
          },
          {
            path: 'requests/:id',
            element: (
              <RequirePermission module="DossierRequests">
                <RequestDetailPage />
              </RequirePermission>
            ),
          },
          {
            path: 'my-tasks',
            element: (
              <RequirePermission module="MyTasks">
                <MyTasksPage />
              </RequirePermission>
            ),
          },
          { path: 'my-tasks/:id', element: <TaskPage /> },
          {
            path: 'data-bank',
            element: (
              <RequirePermission module="DataBank">
                <DataBankPage />
              </RequirePermission>
            ),
          },
          {
            path: 'technical-documents',
            element: (
              <RequirePermission module="TechnicalDocuments">
                <LibraryPage key="technical_documents" library="technical_documents" />
              </RequirePermission>
            ),
          },
          {
            path: 'pharmacopoeia',
            element: (
              <RequirePermission module="Pharmacopoeia">
                <LibraryPage key="pharmacopoeia" library="pharmacopoeia" />
              </RequirePermission>
            ),
          },
          {
            path: 'users',
            element: (
              <RequirePermission module="Users">
                <UsersPage />
              </RequirePermission>
            ),
          },
          {
            path: 'exporters',
            element: (
              <RequirePermission module="Exporters">
                <ExportersPage />
              </RequirePermission>
            ),
          },
          {
            path: 'exporters/:id',
            element: (
              <RequirePermission module="Exporters">
                <ExporterDetailPage />
              </RequirePermission>
            ),
          },
          {
            path: 'access-control',
            element: (
              <RequirePermission module="AccessControl">
                <AccessControlPage />
              </RequirePermission>
            ),
          },
          {
            path: 'activity',
            element: (
              <RequirePermission module="ActivityLog">
                <ActivityLogPage />
              </RequirePermission>
            ),
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
      // Client portal (exporter logins). Staff landing here are sent to the same page under /internal.
      {
        path: '/',
        element: (
          <RequireAuth loginPath="/login">
            <RequireClient>
              <ClientLayout />
            </RequireClient>
          </RequireAuth>
        ),
        children: [
          { index: true, element: <ClientDashboardPage /> },
          { path: 'requests', element: <ClientDossiersPage /> },
          { path: 'requisitions', element: <ClientRequisitionsPage /> },
          { path: 'requests/new', element: <ClientNewRequestPage /> },
          { path: 'downloads', element: <ClientDownloadsPage /> },
          { path: 'requests/:id', element: <ClientRequestPage /> },
          { path: 'notifications', element: <ClientNotificationsPage /> },
          { path: 'account', element: <ClientAccountPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);

export function App() {
  return (
    <MantineProvider theme={theme} defaultColorScheme="light">
      <DatesProvider settings={{ firstDayOfWeek: 1 }}>
        <QueryClientProvider client={queryClient}>
          <ModalsProvider>
            <Notifications position="top-right" limit={4} />
            <RouterProvider router={router} />
          </ModalsProvider>
        </QueryClientProvider>
      </DatesProvider>
    </MantineProvider>
  );
}
